import { DocumentTemplateShell } from './TemplateShared';
import {
  TemplateAppendixLayout,
  TemplateSummaryHeader,
} from './TemplateAppendixLayout';
import { TemplatePractitionerBlock } from './TemplatePractitionerBlock';
import { DASH, EMPTY, NOT_AVAILABLE, formatLongDate } from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type SocialHistoryPractitioner = TemplatePractitioner;

export interface SocialHistoryEntry {
  id?: string;
  name?: string;
  value?: string;
  notes?: string;
  observationDate?: string;
  observationsRecorded?: string;
  practitioner?: SocialHistoryPractitioner;
}

export interface SocialHistoriesTemplateProps {
  entries: SocialHistoryEntry[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightEntryId?: string;
}

const summaryHeaders = ['Name', 'Value', 'Date', 'Page'] as const;

const SummaryTable = ({
  entries,
  highlightEntryId,
}: {
  entries: SocialHistoryEntry[];
  highlightEntryId?: string;
}) => (
  <div className="overflow-hidden rounded-sm border border-zinc-200">
    <div className="flex bg-slate-100">
      {summaryHeaders.map((h, idx) => (
        <div
          key={h}
          className={`flex flex-1 px-3 py-2.5 ${
            idx < summaryHeaders.length - 1 ? 'border-r border-zinc-300' : ''
          }`}
        >
          <p className="flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            {h}
          </p>
        </div>
      ))}
    </div>
    {entries.map((entry, idx) => {
      const isHighlighted = !!highlightEntryId && entry.id === highlightEntryId;
      const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
      const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
      return (
        <div
          key={entry.id ?? `sh-${idx}`}
          className={`flex border-t border-zinc-200 ${rowBg}`}
        >
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.name?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {entry.value?.trim() || DASH}
            </p>
          </div>
          <div className="flex flex-1 border-r border-zinc-200 px-3 py-2.5">
            <p className="flex-1 text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {formatLongDate(entry.observationDate)}
            </p>
          </div>
          <div className="flex flex-1 px-3 py-2.5">
            <a
              href={`#social-history-${entry.id}`}
              className="flex-1 text-[10px] leading-tight text-blue-600"
            >
              {idx + 2}
            </a>
          </div>
        </div>
      );
    })}
  </div>
);

const AppendixCard = ({
  entry,
  highlightEntryId,
}: {
  entry: SocialHistoryEntry;
  highlightEntryId?: string;
}) => {
  const isHighlighted = !!highlightEntryId && entry.id === highlightEntryId;
  return (
    <article
      id={`social-history-${entry.id}`}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white pb-4`}
    >
      <header className="flex flex-col gap-1.5 bg-slate-700 px-5 pt-5 pb-3 text-white">
        <p className="text-[14px] leading-tight font-semibold">
          {entry.name?.trim() || EMPTY}
        </p>
        <p className="text-[10px] leading-tight">
          <span className="font-semibold">Observations recorded:</span>{' '}
          {entry.observationsRecorded?.trim() || NOT_AVAILABLE}
        </p>
      </header>

      <div className="mx-5 mt-3 flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white px-4 py-3">
        <div className="flex flex-col gap-1">
          <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
            Value
          </p>
          <p className="text-[11px] leading-tight whitespace-pre-line text-zinc-950">
            {entry.value?.trim() || NOT_AVAILABLE}
          </p>
        </div>

        <div className="h-px w-full bg-zinc-200" />

        <div className="flex flex-col gap-1">
          <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
            Notes
          </p>
          <p className="text-[11px] leading-tight whitespace-pre-line text-zinc-950">
            {entry.notes?.trim() || NOT_AVAILABLE}
          </p>
        </div>

        <div className="h-px w-full bg-zinc-200" />

        <TemplatePractitionerBlock practitioner={entry.practitioner} />
      </div>
    </article>
  );
};

export const SocialHistoriesTemplate = ({
  entries,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightEntryId,
}: SocialHistoriesTemplateProps) => {
  const items = entries ?? [];
  const hasItems = items.length > 0;

  return (
    <DocumentTemplateShell
      title="Social Histories Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <TemplateAppendixLayout
        hasItems={hasItems}
        emptyMessage="No social histories available"
        summaryHeader={
          <TemplateSummaryHeader
            title="All Reported Social Histories"
            subtitle="Sorted alphabetically"
            note="*Refer to appendix below for more details"
          />
        }
        summaryTable={
          <SummaryTable entries={items} highlightEntryId={highlightEntryId} />
        }
        appendix={items.map((entry) => (
          <AppendixCard
            key={entry.id}
            entry={entry}
            highlightEntryId={highlightEntryId}
          />
        ))}
      />
    </DocumentTemplateShell>
  );
};
